/**
 * modal.ts — открытие/закрытие модалки товара, навигация изображений, focus-trap
 * Проект: TorgiRT (shared — все витрины)
 */

import { state, productLookup } from './state';
import { getConfig, normalizeProduct } from './helpers';
import { findProduct } from './cart';
import { renderModal } from './render';
import { showFlash } from './toast';

let _modalAbort: AbortController | null = null;
let _focusBeforeModal: HTMLElement | null = null;
let _trapBound = false;

const FOCUSABLE_SEL =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function getModalRoot(): HTMLElement | null {
  return document.getElementById('product-modal');
}

function getFocusables(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SEL)).filter(
    (el) => el.offsetParent !== null || el === document.activeElement
  );
}

function trapFocus(event: KeyboardEvent): void {
  if (!state.selectedProduct || event.key !== 'Tab') return;
  const root = getModalRoot();
  if (!root || root.classList.contains('hidden')) return;

  // Prefer image-viewer subtree when open
  const scope =
    state.imageViewerOpen
      ? (root.querySelector('.image-viewer, [data-image-viewer]') as HTMLElement | null) ?? root
      : (root.querySelector('.modal-dialog, [role="dialog"]') as HTMLElement | null) ?? root;

  const focusables = getFocusables(scope);
  if (focusables.length === 0) {
    event.preventDefault();
    scope.focus?.();
    return;
  }

  const first = focusables[0];
  const last = focusables[focusables.length - 1];
  const active = document.activeElement as HTMLElement | null;

  if (event.shiftKey) {
    if (!active || active === first || !scope.contains(active)) {
      event.preventDefault();
      last.focus();
    }
  } else if (!active || active === last || !scope.contains(active)) {
    event.preventDefault();
    first.focus();
  }
}

function ensureTrapBound(): void {
  if (_trapBound) return;
  document.addEventListener('keydown', trapFocus, true);
  _trapBound = true;
}

function focusFirstInModal(): void {
  const root = getModalRoot();
  if (!root) return;
  const dialog = (root.querySelector('.modal-dialog, [role="dialog"]') as HTMLElement | null) ?? root;
  dialog.setAttribute('role', 'dialog');
  dialog.setAttribute('aria-modal', 'true');
  const titleEl = dialog.querySelector('#modal-product-title, .modal-title');
  if (titleEl && titleEl.id) {
    dialog.setAttribute('aria-labelledby', titleEl.id);
  } else if (titleEl) {
    titleEl.id = 'modal-product-title';
    dialog.setAttribute('aria-labelledby', 'modal-product-title');
  }
  if (!dialog.hasAttribute('tabindex')) dialog.setAttribute('tabindex', '-1');

  const focusables = getFocusables(dialog);
  const preferred =
    focusables.find((el) => el.getAttribute('data-action') === 'close-modal' || el.classList.contains('modal-close')) ??
    focusables[0] ??
    dialog;
  preferred.focus();
}

export function openProductModal(article: string): void {
  _focusBeforeModal = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  ensureTrapBound();

  const fromCart = state.cart.find((item) => item.article === article);
  const product = fromCart ?? findProduct(article);
  if (product) {
    state.selectedProduct = { ...product, images: product.images ?? [] } as typeof state.selectedProduct;
    state.currentImageIndex = 0;
    preloadAdjacentImages();
    // renderModal вызывается снаружи; фокус поставим после микротаска
    queueMicrotask(focusFirstInModal);
    return;
  }

  if (_modalAbort) _modalAbort.abort();
  _modalAbort = new AbortController();

  const jsonUrl = getConfig("ajaxProductJsonUrl", "") as string;
  fetch(jsonUrl + "?article=" + encodeURIComponent(article), { signal: _modalAbort.signal })
    .then((res) => {
      if (!res.ok) throw new Error("HTTP " + res.status);
      return res.json();
    })
    .then((data) => {
      _modalAbort = null;
      if (!data.article) return;
      const loaded = normalizeProduct(data);
      productLookup.set(loaded.article, loaded);
      state.selectedProduct = loaded;
      state.currentImageIndex = 0;
      preloadAdjacentImages();
      renderModal();
      queueMicrotask(focusFirstInModal);
    })
    .catch((err: any) => {
      if (err.name === 'AbortError') return;
      showFlash("Не удалось загрузить товар", 'error');
    });
}

export function closeProductModal(): void {
  state.selectedProduct = null;
  state.currentImageIndex = 0;
  state.imageViewerOpen = false;
  const restore = _focusBeforeModal;
  _focusBeforeModal = null;
  queueMicrotask(() => {
    if (restore && document.contains(restore)) {
      restore.focus();
    }
  });
}

export function shiftModalImage(delta: number): void {
  if (!state.selectedProduct) return;
  const len = (state.selectedProduct.images ?? []).length;
  if (!len) return;
  state.currentImageIndex = (state.currentImageIndex + delta + len) % len;
  preloadAdjacentImages();
}

function preloadAdjacentImages(): void {
  if (!state.selectedProduct) return;
  const images = state.selectedProduct.images ?? [];
  const len = images.length;
  if (len <= 1) return;
  const idx = state.currentImageIndex;
  const toPreload = [
    images[(idx + 1) % len],
    images[(idx - 1 + len) % len],
  ];
  for (const src of toPreload) {
    if (src) {
      const img = new Image();
      img.src = src;
    }
  }
}
